import React, { useState } from 'react';
import axios from 'axios';
import { Search, MapPin, Utensils, Plus, Star } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RestaurantFinder = ({ token }) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [selectedMeal, setSelectedMeal] = useState(null);

  const searchRestaurants = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/restaurants/search?query=${encodeURIComponent(searchQuery)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRestaurants(response.data.restaurants || []);
    } catch (error) {
      console.error('Error searching restaurants:', error);
      alert('Failed to search restaurants');
    } finally {
      setLoading(false);
    }
  };

  const addRestaurantMeal = async () => {
    if (!selectedRestaurant || !selectedMeal) return;

    try {
      await axios.post(
        `${API}/restaurants/add-meal`,
        {
          restaurant_name: selectedRestaurant.name,
          meal_name: selectedMeal.name,
          calories: selectedMeal.calories,
          protein: selectedMeal.protein || 0,
          carbs: selectedMeal.carbs || 0,
          fats: selectedMeal.fats || 0
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Meal logged successfully! 🍽️');
      setSelectedRestaurant(null);
      setSelectedMeal(null);
    } catch (error) {
      console.error('Error adding meal:', error);
      alert('Failed to log meal');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      searchRestaurants();
    }
  };

  return (
    <div className="space-y-4" data-testid="restaurant-finder">
      {/* Search Section */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
        <h2 className="text-lg font-bold mb-3 flex items-center gap-2">
          <Utensils size={20} className="text-[#FF6600]" />
          Restaurant Finder
        </h2>
        
        <div className="flex gap-3">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={t('restaurant.searchPlaceholder')}
            className="flex-1 p-3 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:border-[#FF6600]"
            data-testid="search-input"
          />
          <button
            onClick={searchRestaurants}
            disabled={loading}
            className="px-4 py-2 bg-[#FF6600] text-white rounded-lg hover:bg-[#E55C00] disabled:opacity-50"
            data-testid="search-btn"
          >
            <Search size={20} />
          </button>
        </div>
      </div>

      {/* Results */}
      {restaurants.length > 0 && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm">
          <h3 className="font-bold mb-4">Search Results</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {restaurants.map((restaurant, idx) => (
              <div
                key={idx}
                className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-[#FF6600] transition-all cursor-pointer"
                onClick={() => setSelectedRestaurant(restaurant)}
              >
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h4 className="font-bold mb-1">{restaurant.name}</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                      <MapPin size={12} />
                      {restaurant.location || 'Location not specified'}
                    </p>
                  </div>
                  {restaurant.rating && (
                    <div className="flex items-center gap-1 bg-orange-100 px-2 py-1 rounded">
                      <Star size={12} className="text-[#FF6600] fill-current" />
                      <span className="text-xs font-semibold">{restaurant.rating}</span>
                    </div>
                  )}
                </div>
                
                <p className="text-xs text-gray-600 dark:text-gray-300 mb-3">{restaurant.cuisine_type}</p>
                
                {restaurant.menu_items && restaurant.menu_items.length > 0 && (
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {restaurant.menu_items.length} menu item{restaurant.menu_items.length !== 1 ? 's' : ''} available
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Selected Restaurant Menu */}
      {selectedRestaurant && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm border-2 border-[#FF6600]">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold">{selectedRestaurant.name} Menu</h3>
            <button
              onClick={() => setSelectedRestaurant(null)}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 dark:bg-gray-900"
            >
              Close
            </button>
          </div>

          <div className="space-y-3">
            {selectedRestaurant.menu_items && selectedRestaurant.menu_items.length > 0 ? (
              selectedRestaurant.menu_items.map((item, idx) => (
                <div
                  key={idx}
                  className={`border rounded-lg p-4 transition-all cursor-pointer ${
                    selectedMeal?.name === item.name
                      ? 'border-[#FF6600] bg-orange-50'
                      : 'border-gray-200 dark:border-gray-700 hover:border-gray-400'
                  }`}
                  onClick={() => setSelectedMeal(item)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h4 className="font-semibold text-sm mb-1">{item.name}</h4>
                      {item.description && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{item.description}</p>
                      )}
                      <div className="flex gap-4 text-xs text-gray-600 dark:text-gray-300">
                        <span>🔥 {item.calories} cal</span>
                        {item.protein && <span>💪 {item.protein}g protein</span>}
                        {item.carbs && <span>🍞 {item.carbs}g carbs</span>}
                        {item.fats && <span>🥑 {item.fats}g fats</span>}
                      </div>
                    </div>
                    {selectedMeal?.name === item.name && (
                      <div className="ml-3">
                        <div className="w-6 h-6 bg-[#FF6600] rounded-full flex items-center justify-center">
                          <Plus size={14} className="text-white" />
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-gray-500 dark:text-gray-400 text-sm py-8">
                No menu items available for this restaurant
              </p>
            )}
          </div>

          {selectedMeal && (
            <button
              onClick={addRestaurantMeal}
              className="w-full mt-4 py-3 bg-[#FF6600] text-white font-semibold rounded-lg hover:bg-[#E55C00]"
            >
              Log "{selectedMeal.name}" to My Meals
            </button>
          )}
        </div>
      )}

      {loading && (
        <div className="text-center py-8">
          <div className="text-gray-500 dark:text-gray-400">Searching restaurants...</div>
        </div>
      )}

      {!loading && restaurants.length === 0 && searchQuery && (
        <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-sm text-center">
          <Utensils size={48} className="mx-auto text-gray-400 mb-4" />
          <p className="text-gray-500 dark:text-gray-400">No restaurants found. Try a different search!</p>
        </div>
      )}
    </div>
  );
};

export default RestaurantFinder;
